import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Car, ArrowLeft, Loader2, ClipboardList, CheckCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/PageHeader"
import { MicrofoneButton } from "@/components/ai/MicrofoneButton"
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition"
import { useAuth } from "@/contexts/AuthContext"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import { normalizarPlaca } from "@/lib/aiAssistantService"
import { validarPlaca } from "@/lib/fipeService"
import { criarAvaliacaoParaVeiculo } from "@/lib/avaliacoes"
import { buscarVeiculoPorPlaca, type VeiculoResumo } from "@/lib/veiculoSearch"
import { toast } from "sonner"

type Acao = "cadastrar" | "avaliar"

type Etapa =
  | "menu"
  | "capturando"
  | "processando"
  | "confirmando_placa"
  | "buscando_veiculo"
  | "confirmando_veiculo"
  | "veiculo_nao_encontrado"
  | "criando_avaliacao"
  | "manual"

export default function AssistenteIA() {
  const navigate = useNavigate()
  const { perfil, loja } = useAuth()
  const speech = useSpeechRecognition()

  const [etapa, setEtapa] = useState<Etapa>("menu")
  const [acao, setAcao] = useState<Acao>("cadastrar")
  const [placaConfirmada, setPlacaConfirmada] = useState<string | null>(null)
  const [placaManual, setPlacaManual] = useState("")
  const [erroProcessamento, setErroProcessamento] = useState<string | null>(null)
  const [veiculoEncontrado, setVeiculoEncontrado] = useState<VeiculoResumo | null>(null)

  const vertical = useTipoOperacao()
  const ultimoTextoRef = useRef("")
  const nome = perfil?.nome?.split(" ")[0] ?? "você"

  useEffect(() => {
    const texto = speech.transcript || speech.interimTranscript
    if (texto) ultimoTextoRef.current = texto
  }, [speech.transcript, speech.interimTranscript])

  useEffect(() => {
    if (etapa !== "capturando") return
    if (speech.state !== "idle") return
    const texto = ultimoTextoRef.current.trim()
    if (!texto) return
    void processarTranscricao(texto)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speech.state, etapa])

  const iniciarCaptura = (acaoSelecionada: Acao) => {
    ultimoTextoRef.current = ""
    setAcao(acaoSelecionada)
    setEtapa("capturando")
    setPlacaConfirmada(null)
    setVeiculoEncontrado(null)
    setErroProcessamento(null)
    setPlacaManual("")
    speech.reset()
  }

  const voltarMenu = () => {
    speech.stop()
    speech.reset()
    ultimoTextoRef.current = ""
    setEtapa("menu")
    setPlacaConfirmada(null)
    setVeiculoEncontrado(null)
    setErroProcessamento(null)
    setPlacaManual("")
  }

  const processarTranscricao = async (texto: string) => {
    setEtapa("processando")
    try {
      const result = await normalizarPlaca(texto)
      if (!result.placa || !validarPlaca(result.placa)) {
        setErroProcessamento("Não entendi a placa. Tente de novo ou digite.")
        setPlacaManual(texto)
        setEtapa("manual")
        return
      }
      setPlacaConfirmada(result.placa)
      setEtapa("confirmando_placa")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao processar voz"
      toast.error(message)
      setErroProcessamento(message)
      setEtapa("manual")
    }
  }

  const confirmarPlaca = async (placa: string) => {
    if (acao === "cadastrar") {
      navigate("/novo-veiculo", { state: { placaPreenchidaPorIA: placa } })
      return
    }

    if (!loja?.id) {
      toast.error("Sessão inválida. Faça login novamente.")
      return
    }
    setEtapa("buscando_veiculo")
    try {
      const veiculo = await buscarVeiculoPorPlaca(placa, loja.id)
      if (!veiculo) {
        setEtapa("veiculo_nao_encontrado")
        return
      }
      setVeiculoEncontrado(veiculo)
      setEtapa("confirmando_veiculo")
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao buscar veículo"
      toast.error(message)
      setErroProcessamento(message)
      setEtapa("manual")
    }
  }

  const confirmarVeiculo = async (veiculo: VeiculoResumo) => {
    if (!loja?.id) return
    setEtapa("criando_avaliacao")
    try {
      const { id, reusedExisting } = await criarAvaliacaoParaVeiculo({
        lojaId: loja.id,
        veiculoId: veiculo.id,
      })
      if (reusedExisting) {
        toast.info("Retomando avaliação em andamento.")
      }
      navigate(`/avaliacao/${id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao criar avaliação"
      toast.error(message)
      voltarMenu()
    }
  }

  const tentarPlacaManual = () => {
    const limpa = placaManual.toUpperCase().replace(/[^A-Z0-9]/g, "")
    if (!validarPlaca(limpa)) {
      toast.error("Placa inválida. Use o formato ABC1234 ou ABC1D23.")
      return
    }
    void confirmarPlaca(limpa)
  }

  return (
    <div className="container mx-auto max-w-2xl space-y-6 p-4 md:p-8">
      <PageHeader
        title="Assistente IA"
        description="Use sua voz para cadastrar mais rápido"
      />

      {etapa === "menu" && (
        <Card>
          <CardHeader>
            <CardTitle>Olá, {nome}. O que você quer fazer?</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Button
              size="lg"
              className="h-20 w-full text-base"
              onClick={() => iniciarCaptura("cadastrar")}
            >
              <Car className="mr-3 h-6 w-6" />
              Cadastrar Veículo
            </Button>
            <Button
              size="lg"
              variant="secondary"
              className="h-20 w-full text-base"
              onClick={() => iniciarCaptura("avaliar")}
            >
              <ClipboardList className="mr-3 h-6 w-6" />
              {vertical.labels.novoRegistro}
            </Button>
          </CardContent>
        </Card>
      )}

      {etapa === "capturando" && (
        <Card>
          <CardHeader>
            <CardTitle>
              {acao === "cadastrar"
                ? "Fale a placa do veículo"
                : `Fale a placa para ${vertical.labels.labelPrincipal.toLowerCase()}`}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center space-y-6 py-8">
            <MicrofoneButton
              isListening={speech.state === "listening"}
              isSupported={speech.isSupported}
              onStart={speech.start}
              onStop={speech.stop}
            />
            <div className="min-h-12 w-full rounded border bg-muted/30 p-3 text-center text-lg">
              {speech.transcript || speech.interimTranscript || (
                <span className="text-muted-foreground">
                  {speech.state === "listening" ? "Ouvindo..." : "Aperte o microfone e fale"}
                </span>
              )}
            </div>
            {!speech.isSupported && (
              <p className="text-sm text-muted-foreground">
                Seu navegador não suporta reconhecimento de voz. Digite a placa abaixo.
              </p>
            )}
            <div className="w-full space-y-2">
              <Input
                placeholder="Ou digite a placa aqui"
                value={placaManual}
                onChange={(e) => setPlacaManual(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                maxLength={7}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
              <Button
                variant="secondary"
                className="w-full"
                onClick={tentarPlacaManual}
                disabled={!placaManual.trim()}
              >
                Usar essa placa
              </Button>
            </div>
            <Button variant="ghost" onClick={voltarMenu}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar
            </Button>
          </CardContent>
        </Card>
      )}

      {etapa === "processando" && (
        <Card>
          <CardContent className="flex flex-col items-center space-y-3 py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-muted-foreground">Entendendo a placa...</p>
          </CardContent>
        </Card>
      )}

      {etapa === "confirmando_placa" && placaConfirmada && (
        <Card>
          <CardHeader>
            <CardTitle>É essa a placa?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 py-6">
            <div className="rounded-lg border-2 border-primary bg-primary/5 p-6 text-center">
              <p className="text-4xl font-bold tracking-widest">{placaConfirmada}</p>
            </div>
            <p className="text-center text-muted-foreground">
              {acao === "cadastrar"
                ? "Vou abrir o cadastro do veículo com essa placa."
                : `Vou buscar esse veículo para ${vertical.labels.iniciarAcao.toLowerCase()}.`}
            </p>
            <div className="flex flex-col gap-3">
              <Button size="lg" onClick={() => void confirmarPlaca(placaConfirmada)}>
                Sim, continuar
              </Button>
              <Button variant="outline" onClick={() => iniciarCaptura(acao)}>
                Não, falar de novo
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {etapa === "buscando_veiculo" && (
        <Card>
          <CardContent className="flex flex-col items-center space-y-3 py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-muted-foreground">Buscando veículo...</p>
          </CardContent>
        </Card>
      )}

      {etapa === "confirmando_veiculo" && veiculoEncontrado && (
        <Card>
          <CardHeader>
            <CardTitle>É esse o veículo?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 py-6">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-1">
              <p className="text-lg font-semibold">
                {veiculoEncontrado.marca} {veiculoEncontrado.modelo}
              </p>
              <p className="text-sm text-muted-foreground">
                {veiculoEncontrado.ano} · {veiculoEncontrado.cor ?? "—"}
              </p>
              <p className="text-sm font-mono font-medium">{veiculoEncontrado.placa}</p>
            </div>
            <div className="flex flex-col gap-3">
              <Button size="lg" onClick={() => void confirmarVeiculo(veiculoEncontrado)}>
                <CheckCircle className="mr-2 h-5 w-5" />
                Sim, {vertical.labels.iniciarAcao.toLowerCase()}
              </Button>
              <Button variant="outline" onClick={() => iniciarCaptura("avaliar")}>
                Não, buscar outro
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {etapa === "veiculo_nao_encontrado" && (
        <Card>
          <CardHeader>
            <CardTitle>Veículo não encontrado</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 py-6">
            <p className="text-muted-foreground text-center">
              A placa <span className="font-mono font-semibold">{placaConfirmada}</span> não está
              cadastrada nessa loja. Cadastre o veículo primeiro.
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  if (placaConfirmada) {
                    navigate("/novo-veiculo", { state: { placaPreenchidaPorIA: placaConfirmada } })
                  }
                }}
              >
                <Car className="mr-2 h-4 w-4" />
                Cadastrar veículo agora
              </Button>
              <Button variant="outline" onClick={() => iniciarCaptura("avaliar")}>
                Tentar outra placa
              </Button>
              <Button variant="ghost" onClick={voltarMenu}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar ao menu
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {etapa === "criando_avaliacao" && (
        <Card>
          <CardContent className="flex flex-col items-center space-y-3 py-12">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-muted-foreground">Abrindo avaliação...</p>
          </CardContent>
        </Card>
      )}

      {etapa === "manual" && (
        <Card>
          <CardHeader>
            <CardTitle>Digite a placa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 py-6">
            {erroProcessamento && (
              <p className="text-sm text-destructive">{erroProcessamento}</p>
            )}
            <Input
              placeholder="Ex: ABC1D23"
              value={placaManual}
              onChange={(e) => setPlacaManual(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
              maxLength={7}
              autoFocus
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
            <div className="flex flex-col gap-3">
              <Button onClick={tentarPlacaManual} disabled={!placaManual.trim()}>
                Continuar
              </Button>
              <Button variant="outline" onClick={() => iniciarCaptura(acao)}>
                Tentar por voz de novo
              </Button>
              <Button variant="ghost" onClick={voltarMenu}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
